import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import { FRAME_MEMBER_THICKNESS_METRES } from '../core/SceneRoot';
import type { RoomFloorplanDto } from '../generated/contracts';
import { buildLabelledPlate, disposePlate } from './LabelledPlate';
import type { Grabber, GrabberHub } from './Grabbers';

/**
 * The only in-headset way to leave a session (plan/open-items.md, "there is no in-headset way
 * to leave a session short of removing the headset"). Before this, the only exit was
 * `sessionScreen`'s own "leave" button — a 2D element in the browser window, invisible to
 * anyone actually wearing the headset. Physically removing the headset does not clean anything
 * up: the XR session stays live, any node still held stays locked, and the room keeps counting
 * that peer as present. This gives the same `XrManager.exitAsync()` the browser button already
 * calls (`main.ts`'s own `sessionScreen.onExitRequested`) a mount inside the room itself, so
 * leaving from inside a session and leaving from the browser both end up going through exactly
 * one teardown path.
 *
 * **Where it lives, and why.** The owner, after the room-name lettering moved onto the north
 * wall's own top beam: "I think we now have established a non-admin place for info and
 * important buttons, above the whiteboard." Unlike `DebugPanel` (south wall, `lars`-only, a
 * dozen buttons), this is a public fixture — everyone in the room needs a way out, not just the
 * owner — so it mounts on the *north* wall, in that same public information column, not beside
 * `DebugPanel`.
 *
 * **How it avoids the room name.** Not by staying clear of the lettering's own horizontal
 * extent — a room name can paint up to `BEAM_NAME_MAX_TEXT_WIDTH_FRACTION` (50%) of the beam's
 * own width, centred, so any fixed x-offset large enough to clear a long name in an 8m room
 * would not clear one in a wider template. Instead this sign sits in a different *vertical*
 * band entirely: the room name is painted only onto the top beam's own mesh
 * (`RoomPlaque.buildBeamNameTexture`, `y` from `wallHeight - FRAME_MEMBER_THICKNESS_METRES` to
 * `wallHeight`), and this sign is a wholly separate mesh mounted below that beam's bottom edge,
 * with `SIGN_GAP_BELOW_BEAM_METRES` of clearance. Two different meshes in two disjoint `y`
 * ranges cannot collide regardless of room width or name length — a geometric guarantee, not an
 * inference about how long a name is likely to be. Centred at `x = 0`, directly below the name
 * above it, matching the "same public column" the owner described — see `reposition()`'s own
 * doc comment for the exact derivation and why the vertical gap is never smaller than
 * `SIGN_GAP_BELOW_BEAM_METRES + SIGN_HEIGHT` for any template `WhiteboardLayout.computeWhiteboardLayout`
 * can produce.
 *
 * **Why aim-and-hold, not a single press.** The brief is explicit: leaving must be deliberate,
 * never something a brush past can trigger. `DebugPanel`'s own destructive buttons (reset
 * shapes, reset whiteboard) already establish exactly this shape in this project — aim the far
 * button at a fixed wall plate and keep it pressed for `HOLD_MS`, with the plate visibly
 * growing as the hold fills (`HOLD_MAX_SCALE_BUMP`) so the delay reads as "in progress," not
 * "broken." Reused verbatim rather than inventing a second gesture: a glancing press, or aiming
 * past the sign on the way to something else, cannot end the session — only sustained, aimed
 * intent for the better part of a second can. `AIM_TOLERANCE`/`MIN_AIM_DISTANCE`/
 * `MAX_AIM_DISTANCE` are the same values `DebugPanel` already proved reachable from across a
 * real room.
 *
 * **What this class does and does not own.** Firing the hold calls `onLeaveRequested` and
 * nothing else — it does not know about `PossessionManager`, `XrManager`, or the hub, the same
 * "callback for a fact with nowhere else to live" shape `DebugPanel.onAccentChanged`/
 * `onDebugGlowChanged` already use. `main.ts` wires the callback to release whatever this
 * client is holding and then call the exact same `xr.exitAsync()` the browser's own exit button
 * calls — see that wiring's own comment for why releasing first matters even though
 * `RoomSyncHub.OnDisconnectedAsync` would eventually catch a held lock anyway.
 */

/** Matches `WhiteboardLayout.ts`'s and `DebugPanel.ts`'s own identical constant and reasoning —
 *  restated, not shared, the same "cannot import a private number" situation those two files
 *  are already in with each other. */
const WALL_THICKNESS = 0.1;

/** Gap kept between the wall's own interior face and the sign, so nothing z-fights — matches
 *  `WhiteboardLayout.ts`'s and `DebugPanel.ts`'s identical constant. */
const WALL_CLEARANCE = 0.01;

/**
 * Vertical clearance kept between the top beam's own bottom edge and this sign's own top edge —
 * see this file's own module comment for why a vertical gap, rather than a horizontal offset,
 * is what actually guarantees no collision with the room name. Small, because the available
 * gap itself is small: `WhiteboardLayout.computeWhiteboardLayout`'s own `BOARD_CEILING_RESERVE_METRES`
 * (0.35m) plus `BOARD_TOP_CLEARANCE` (0.1m) guarantee at least 0.45m between the board's own top
 * frame edge and the ceiling for any `wallHeight` below 2.95m, and at least
 * `wallHeight - 2.71` above it (2.71 = the shipped 8x8x3 template's own board-top-frame-to-ceiling
 * figure) — the shipped template alone leaves roughly 0.29m of plain wall between the board's own
 * frame and the beam's own bottom edge, so this sign's own footprint (`SIGN_GAP_BELOW_BEAM_METRES`
 * plus `SIGN_HEIGHT`) has to fit comfortably inside that, with room to spare, for the one template
 * this project actually ships.
 */
const SIGN_GAP_BELOW_BEAM_METRES = 0.03;

const SIGN_WIDTH = 0.5;
const SIGN_HEIGHT = 0.12;

/** Same tolerance `DebugPanel.ts` uses for its own fixed wall plates — proven reachable from
 *  across a real room by that panel's own aim tests. */
const AIM_TOLERANCE = 0.12;
const MIN_AIM_DISTANCE = 0.15;
const MAX_AIM_DISTANCE = 8;

/**
 * How long the far button must stay aimed and held before the session actually ends — longer
 * than `DebugPanel`'s own destructive-action hold (700ms): ending your own presence in the room
 * deserves at least as much deliberation as deleting a shape. See this file's own module
 * comment for why a hold at all, not a single press.
 */
const HOLD_MS = 900;

/** Grows as the hold fills — same "show the delay running" reasoning `DebugPanel.ts` gives for
 *  its own identical constant. */
const HOLD_MAX_SCALE_BUMP = 0.35;

const LABEL = 'leave session';
const BACKGROUND = '#FF5D8F';
const INK = '#12151A';

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// spec: SPEC-073
export class ExitSign {
  private readonly root: TransformNode;
  private readonly mesh: Mesh;

  /** Set once, by `reposition()`, when the room's own floorplan is first known — mirrors
   *  `DebugPanel.positioned`/`RoomPlaque`'s own "the mesh exists before it is placed" split. */
  private positioned = false;

  /** The aimed-and-held sign, if any — tracks which grabber owns the hold, the same "one owner
   *  at a time" shape `DebugPanel.holdFill` uses, so a second grabber aiming at the sign
   *  mid-hold cannot steal or reset someone else's progress. Only one button exists here, so
   *  unlike `DebugPanel.holdFill` this does not also need to track *which* button. */
  private holdFill: { grabberId: string; since: number } | null = null;

  /** Fired once the hold completes — see this file's own module comment for what `main.ts`
   *  wires this to and why this class itself calls neither the hub nor `XrManager`. */
  onLeaveRequested: (() => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
  ) {
    this.root = new TransformNode('exit-sign', scene);
    this.root.setEnabled(false);

    this.mesh = buildLabelledPlate(scene, 'exit-sign-leave', LABEL, BACKGROUND, INK, SIGN_WIDTH, SIGN_HEIGHT);
    this.mesh.parent = this.root;
    this.mesh.isPickable = false;
  }

  /** Whether the sign is currently positioned and shown — false before the first
   *  `reposition()` call. */
  get isVisible(): boolean {
    return this.positioned;
  }

  /**
   * Places the sign on the north wall for a room of this shape, and shows it. Safe to call
   * repeatedly — `main.ts` calls this once after the room's first `buildRoom()`, and again
   * after any resync whose snapshot could carry a different template, the same treatment
   * `DebugPanel.reposition`/`ErrorLogPanel.reposition` already get.
   *
   * `wallInnerZ`/`mountZ` mirror `WhiteboardLayout.computeWhiteboardLayout`'s own derivation for
   * the north wall's interior face exactly (positive `halfDepth`, moving *inward* — toward
   * room-centre — by subtracting, not adding, `WALL_CLEARANCE`; `DebugPanel`'s own south-wall
   * mirror image adds instead, because that wall sits at negative `z`).
   *
   * No yaw: a `CreatePlane`'s own front face is already -Z (CLAUDE.md, "A Babylon plane's front
   * face is -Z"), and the north wall's room-facing normal is also -Z
   * (`WhiteboardLayout.ts`'s own `normal: new Vector3(0, 0, -1)`) — the two already agree, unlike
   * `DebugPanel`'s south-wall mount, which needs a 180-degree yaw to turn a -Z front into the
   * +Z front that wall requires.
   */
  reposition(floorplan: RoomFloorplanDto): void {
    const halfDepth = floorplan.depth / 2;
    const wallInnerZ = halfDepth - WALL_THICKNESS / 2;
    const mountZ = wallInnerZ - WALL_CLEARANCE;

    // Never above the beam's own bottom edge, and never so low it could reach down into the
    // board's own frame for a very short-walled template — clamped against a floor of the
    // board's own worst-case ceiling reserve (see SIGN_GAP_BELOW_BEAM_METRES's own doc comment)
    // so a future template shorter than the shipped one still cannot push this sign into the
    // whiteboard frame below it.
    const beamBottomY = floorplan.wallHeight - FRAME_MEMBER_THICKNESS_METRES;
    const y = clampNumber(
      beamBottomY - SIGN_GAP_BELOW_BEAM_METRES - SIGN_HEIGHT / 2,
      SIGN_HEIGHT / 2,
      floorplan.wallHeight - SIGN_HEIGHT / 2,
    );

    this.root.position.set(0, y, mountZ);
    this.root.setEnabled(true);
    this.positioned = true;
  }

  /** Advances one tick's worth of aim/hold state. Registered from `main.ts` the same way every
   *  other per-frame gesture reader is — see `start()`. */
  private tick(nowMs: number): void {
    if (!this.positioned) {
      return;
    }

    let holderStillAiming = false;

    for (const grabber of this.grabbers.grabbers) {
      if (!this.isAimedAt(grabber) || !grabber.far.pressed) {
        continue;
      }

      if (this.holdFill !== null && this.holdFill.grabberId === grabber.id) {
        holderStillAiming = true;
      } else if (this.holdFill === null) {
        this.holdFill = { grabberId: grabber.id, since: nowMs };
        holderStillAiming = true;
      } else {
        // A different grabber already owns the fill in progress — matches
        // `DebugPanel.tick`'s own "one owner at a time" rule.
        continue;
      }

      const progress = Math.min(1, (nowMs - this.holdFill.since) / HOLD_MS);
      this.mesh.scaling.setAll(1 + progress * HOLD_MAX_SCALE_BUMP);

      if (progress >= 1) {
        this.holdFill = null;
        this.mesh.scaling.setAll(1);
        this.onLeaveRequested?.();
      }
    }

    if (!holderStillAiming && this.holdFill !== null) {
      this.mesh.scaling.setAll(1);
      this.holdFill = null;
    }
  }

  /** Registered the same way every other per-frame gesture reader in this project is
   *  (`DebugPanel.start`, `ConnectionDrawer.start`) — returns the teardown. */
  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick(performance.now()));
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  /** Whether `grabber`'s aim ray passes close enough to the sign to count — the same
   *  nearest-along-the-ray shape `DebugPanel.buttonUnderAim` uses, simplified to one fixed
   *  target rather than a search over several. */
  private isAimedAt(grabber: Grabber): boolean {
    // Forced, not assumed current — CLAUDE.md's own "An unforced computeWorldMatrix can return
    // last frame's answer" trap: this is a read site, so it forces here rather than trusting
    // whatever `reposition()` last computed.
    this.mesh.computeWorldMatrix(true);
    const position = this.mesh.getAbsolutePosition();

    const toSign = position.subtract(grabber.aimOrigin);
    const along = Vector3.Dot(toSign, grabber.forward);

    if (along < MIN_AIM_DISTANCE || along > MAX_AIM_DISTANCE) {
      return false;
    }

    const closestOnRay = grabber.aimOrigin.add(grabber.forward.scale(along));
    return Vector3.Distance(closestOnRay, position) <= AIM_TOLERANCE;
  }

  /** Frees the mesh's own material and `DynamicTexture` along with itself
   *  (`LabelledPlate.disposePlate` — CLAUDE.md, "`AbstractMesh.dispose()`... does not free
   *  materials"), then the now-childless root. */
  dispose(): void {
    disposePlate(this.mesh);
    this.root.dispose();
  }
}
